import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Fingerprint, Send } from "lucide-react";
import { toast } from "sonner";
import { THREAD_STATUS_LABELS, TOPIC_LABELS, anonymousStatus, publicApi, savedKeys } from "@/lib/publicApi";
import { hasSecureStoreBridge, secretVault } from "@/lib/nativeBridge";
import { formatApiError, formatDate } from "@/lib/api";
import { EmergencyBar } from "./SaudeLayout";
import { Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function AnonymousLookup() {
  const saved = savedKeys.list();
  const [secretKey, setSecretKey] = useState(saved[0]?.secret_key || "");
  const [thread, setThread] = useState(null);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasVaultKey, setHasVaultKey] = useState(false);

  useEffect(() => {
    if (!hasSecureStoreBridge()) return;
    secretVault.has().then((res) => setHasVaultKey(Boolean(res?.ok)));
  }, []);

  const call = async (path, body, successMessage) => {
    setBusy(true);
    try {
      const { data } = await publicApi.post(path, { secret_key: secretKey, ...body });
      if (data?.messages) setThread(data);
      anonymousStatus({ force: true });
      if (successMessage) toast.success(successMessage);
      setError("");
      return data;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const load = async (e) => {
    e?.preventDefault();
    setThread(null);
    await call("/anonymous-questions/access");
  };

  return (
    <div data-testid="anonymous-lookup-page" className="space-y-6">
      <Link to="/saude/fale-com-a-erimus" className="inline-flex items-center gap-2 text-sm text-ink-500" data-testid="lookup-back">
        <ArrowLeft size={16} /> Voltar
      </Link>

      <div>
        <p className="er-label">Consultar resposta</p>
        <h1 className="mt-3 font-display text-2xl font-medium tracking-tight text-ink-900">Chave de acesso</h1>
        <p className="mt-3 text-sm leading-relaxed text-ink-600">
          Sem a chave de acesso não é possível abrir a conversa — nem por nós.
        </p>
      </div>

      <form onSubmit={load} className="space-y-4">
        <div>
          <Label htmlFor="secret" className="text-sm text-ink-600">
            Chave de acesso
          </Label>
          <Input
            id="secret"
            data-testid="lookup-key-input"
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            className="mt-2 h-12 rounded-xl bg-white"
            required
          />
        </div>
        <Button data-testid="lookup-submit" disabled={busy} className="h-12 w-full rounded-full bg-sage-600 text-bone hover:bg-sage-700">
          {busy ? "Consultando…" : "Consultar resposta"}
        </Button>
        {hasVaultKey ? (
          <Button
            type="button"
            variant="outline"
            data-testid="lookup-use-vault-key"
            onClick={async () => {
              const res = await secretVault.get();
              if (res?.ok && res.value) {
                setSecretKey(res.value);
                toast.success("Chave liberada");
              } else {
                toast.error("Não foi possível liberar a chave guardada.");
              }
            }}
            className="h-11 w-full rounded-full"
          >
            <Fingerprint size={16} className="mr-2" /> Usar chave salva (biometria)
          </Button>
        ) : null}
      </form>

      {error ? (
        <p data-testid="lookup-error" role="alert" className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {thread ? (
        <div data-testid="lookup-thread" className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-base font-medium text-ink-900">Sua conversa</p>
              <p className="text-xs text-ink-400">{TOPIC_LABELS[thread.topic] || thread.topic}</p>
            </div>
            <Pill tone={thread.status === "answered" ? "success" : "info"} testId="lookup-thread-status">
              {THREAD_STATUS_LABELS[thread.status] || thread.status}
            </Pill>
          </div>

          <div className="space-y-3">
            {thread.messages.map((m) => (
              <div
                key={m.id}
                data-testid={`lookup-message-${m.id}`}
                className={`rounded-2xl p-4 text-sm leading-relaxed ${
                  m.author === "user" ? "bg-sand/70 text-ink-700" : "bg-white border border-border text-ink-600"
                }`}
              >
                <p className="er-label">{m.author === "user" ? "Você" : "Erimus"}</p>
                <p className="mt-2 whitespace-pre-line">{m.text}</p>
                <p className="mt-2 text-xs text-ink-400">{formatDate(m.created_at)}</p>
              </div>
            ))}
          </div>

          {thread.status !== "closed" ? (
            <>
              <Button
                data-testid="lookup-mark-read"
                onClick={() => call("/anonymous-questions/read", {}, "Marcado como lido").then(load)}
                variant="outline"
                className="h-11 w-full rounded-full"
              >
                <Check size={15} className="mr-2" /> Marcar respostas como lidas
              </Button>

              <div className="space-y-3">
                <Textarea
                  data-testid="lookup-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  placeholder="Enviar uma mensagem complementar (sem dados identificáveis)"
                  className="rounded-xl bg-white"
                />
                <Button
                  data-testid="lookup-reply-submit"
                  disabled={busy || reply.trim().length < 2}
                  onClick={async () => {
                    const ok = await call("/anonymous-questions/messages", { message: reply }, "Mensagem enviada");
                    if (ok) setReply("");
                  }}
                  className="h-12 w-full rounded-full bg-sage-600 text-bone hover:bg-sage-700"
                >
                  <Send size={15} className="mr-2" /> Enviar mensagem
                </Button>
                <Button
                  data-testid="lookup-close-thread"
                  variant="outline"
                  onClick={() => call("/anonymous-questions/close", {}, "Conversa encerrada").then(load)}
                  className="h-11 w-full rounded-full border-danger/40 text-danger hover:bg-danger/10 hover:text-danger"
                >
                  Encerrar esta conversa
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-ink-500">Esta conversa foi encerrada. Se precisar, envie uma nova pergunta.</p>
          )}
        </div>
      ) : null}
      <EmergencyBar />
    </div>
  );
}
